import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

/**
 * Cola de mutations offline en IndexedDB. Cuando el user muta un gasto sin
 * conexión, encolamos la request y devolvemos un optimistic response. Al
 * detectar `online`, el `flush()` reintenta cada entry contra el server.
 *
 * Alcance (Sprint I + L): `createExpense`, `updateExpense`, `deleteExpense`.
 * Un gasto creado offline NO es visible en la lista hasta sincronizar (la
 * lista se refetchea del server), así que update/delete offline solo
 * apuntan a gastos que YA existen en el server — no hace falta rastrear
 * dependencias entre un create encolado y un update/delete posterior. La
 * única colisión posible (varias ops sobre un mismo gasto server) se
 * resuelve por orden de encolado; además `useDeleteExpense` colapsa los
 * updates pendientes de un gasto cuando se encola su borrado.
 *
 * Persistencia: `indexedDB` con schema versionado. Si el user borra el
 * storage del navegador, pierde la cola — trade-off aceptado.
 */

const DB_NAME = 'manusplit-offline';
const DB_VERSION = 1;
const STORE = 'pending-mutations';

export type QueuedMutationKind = 'createExpense' | 'updateExpense' | 'deleteExpense';

export interface QueuedMutation {
  id: string;
  kind: QueuedMutationKind;
  payload: unknown;
  queued_at: number;
  attempts: number;
  last_error?: string | undefined;
}

interface QueueSchema extends DBSchema {
  [STORE]: {
    key: string;
    value: QueuedMutation;
    indexes: { 'by-queued-at': number };
  };
}

let dbPromise: Promise<IDBPDatabase<QueueSchema>> | null = null;

function getDb(): Promise<IDBPDatabase<QueueSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<QueueSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('by-queued-at', 'queued_at');
      },
    });
  }
  return dbPromise;
}

export async function enqueue(kind: QueuedMutation['kind'], payload: unknown): Promise<string> {
  const db = await getDb();
  const id = `q-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  const entry: QueuedMutation = {
    id,
    kind,
    payload,
    queued_at: Date.now(),
    attempts: 0,
  };
  await db.add(STORE, entry);
  notifyListeners();
  return id;
}

export async function getPending(): Promise<QueuedMutation[]> {
  const db = await getDb();
  return db.getAllFromIndex(STORE, 'by-queued-at');
}

export async function pendingCount(): Promise<number> {
  const db = await getDb();
  return db.count(STORE);
}

export async function removePending(id: string): Promise<void> {
  const db = await getDb();
  await db.delete(STORE, id);
  notifyListeners();
}

export async function markFailed(id: string, error: string): Promise<void> {
  const db = await getDb();
  const entry = await db.get(STORE, id);
  if (!entry) return;
  await db.put(STORE, {
    ...entry,
    attempts: entry.attempts + 1,
    last_error: error,
  });
  notifyListeners();
}

// ==================== Change listeners ====================

const listeners = new Set<() => void>();

export function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function notifyListeners() {
  for (const cb of listeners) {
    try { cb(); } catch { /* no-op */ }
  }
}

// ==================== Flush ====================

/**
 * Ejecuta cada entry pendiente pasando su payload al `runner` correspondiente.
 * Éxito → remove. Fallo con excepción → markFailed + salta al siguiente
 * (mejor perder una que bloquear la cola entera).
 *
 * Devuelve `{ ok, fail }` para que la UI pueda mostrar un toast agregado.
 */
export async function flush(
  runners: Partial<Record<QueuedMutation['kind'], (payload: unknown) => Promise<unknown>>>,
): Promise<{ ok: number; fail: number }> {
  const pending = await getPending();
  let ok = 0;
  let fail = 0;
  for (const entry of pending) {
    const runner = runners[entry.kind];
    if (!runner) {
      // No hay runner para este kind — probable versión vieja. Marcamos error.
      await markFailed(entry.id, `no runner for ${entry.kind}`);
      fail++;
      continue;
    }
    try {
      await runner(entry.payload);
      await removePending(entry.id);
      ok++;
    } catch (e) {
      await markFailed(entry.id, e instanceof Error ? e.message : String(e));
      fail++;
    }
  }
  return { ok, fail };
}
